import {isErroneousServerResult} from '@raison/network-facade';
import {MobilePhone, NonEmptyString, Password} from '@raison/types';
import {RsnButton, styles} from '@raison/ui-kit';
import {isLeft} from 'fp-ts/Either';
import React, {ChangeEvent, FormEvent, useEffect, useState} from 'react';
import {useModalsToggler} from '~/entities/dialogs';
import {
    LoginRequestData,
    cancelPendingBiometryActivation,
    get_biometry_type,
    get_touch_id_availability,
    gonative_auth,
    gonative_save_secret,
    login,
} from '~/entities/user';
import InputText from '~/shared/legacy_presentational-components/Form/InputText';
import {required} from '~/shared/legacy_presentational-components/Form/formValidate';
import LoginBiometryStatusModal from '~/shared/legacy_presentational-components/Modals/LoginBiometryStatusModal/LoginBiometryStatusModal';
import LoginErrorModal from '~/shared/legacy_presentational-components/Modals/LoginErrorModal/LoginErrorModal';
import {appLocalStorage} from '~/shared/persistence';
import {getBiometryHelper} from '~/shared/utils/biometryHelper';
import {ErrorHandlerReturnData, getErrorText} from '~/shared/utils/errorHandler';
import {getSavePassHandler} from '~/shared/utils/savePass';

const biometryHelper = getBiometryHelper();
const savePassHandler = getSavePassHandler();

export type PasswordFormProps = {
    changeLoaderState: (visible?: boolean) => void;
};

const PasswordForm = ({changeLoaderState}: PasswordFormProps) => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [serverErrors, setServerErrors] = useState({
        server_error: '',
        server_error_context: '',
        server_error_code: '',
        server_error_link: undefined as string | undefined,
        server_error_type: undefined as ErrorHandlerReturnData['type'] | undefined,
    });
    const [attemptNumber, setAttemptNumber] = useState(1);
    const [isAttempted, setIsAttempted] = useState(false);

    const {changeFlag} = useModalsToggler('loginSignupPage');

    const toggleErrorModal = () => changeFlag('loginErrorModal');
    const toggleBiometryStatusModal = () => changeFlag('loginBiometryStatusModal');

    const biometryType = get_biometry_type();
    const getBiometryTypeText = () => (biometryType === 'faceId' ? 'Face ID' : 'Touch ID');

    useEffect(() => {
        const disposeActBiometryModal = biometryHelper.setActBiometryModalSettings(toggleBiometryStatusModal);
        // a regular fail lets the user try again, only a lockout is worth a dialog
        const disposeOnBiometryFailed = biometryHelper.setOnBiometryFailed(reason => {
            // biometry is reset on a lockout, so the user just goes back to settings
            if (reason === 'lockout') window.restartAppAt('/settings');
        });

        return () => {
            disposeActBiometryModal();
            disposeOnBiometryFailed();
            // the user may try again after a regular fail, but must not leave an unconfirmed secret behind
            cancelPendingBiometryActivation();
        };
    }, []);

    const closeErrorModal = () => {
        toggleErrorModal();
    };

    const signInAsync = async (data: LoginRequestData) => {
        try {
            // if biometry is not available
            get_touch_id_availability(); // It takes some time (500ms)

            setLoading(true);
            changeLoaderState(true);
            const response = await login(data);
            if (isLeft(response)) {
                throw response.left.type === 'ApiClientError' ? response.left.error : {};
            }
            setLoading(false);
            setServerErrors({
                server_error: '',
                server_error_context: '',
                server_error_code: '',
                server_error_link: undefined,
                server_error_type: undefined,
            });

            savePassHandler.setPassword(data.password);

            if (appLocalStorage.hasTouchId.get()) gonative_save_secret();

            if ((attemptNumber === 2 && !appLocalStorage.hasTouchId.get()) || isAttempted) {
                setLoading(false);
                setAttemptNumber(attemptNumber + 1);
                setIsAttempted(true);
                changeLoaderState();
                setServerErrors(prev => ({
                    server_error_code: prev.server_error_code,
                    server_error: 'Something went wrong',
                    server_error_context: `We're sorry, but we couldn't activate biometrics. Don't worry, we'll fix it. Please try activating again later in the “Settings” section.`,
                    server_error_link: undefined,
                    server_error_type: undefined,
                }));
                toggleErrorModal();
                return;
            } else {
                setTimeout(() => {
                    appLocalStorage.callPointBiometry.set('settings');
                    appLocalStorage.runBiometryActivation.set('start');
                    setLoading(false);
                    setAttemptNumber(attemptNumber + 1);
                    changeLoaderState();
                    biometryHelper.setRunAuth(false);
                    gonative_auth();
                }, 500);
            }
        } catch (err) {
            setLoading(false);

            if (isErroneousServerResult(err)) {
                changeLoaderState();
                const {title, context, code} = getErrorText(err);
                setServerErrors({
                    server_error: title,
                    server_error_context: context,
                    server_error_code: code?.toString() || '',
                    server_error_link: undefined,
                    server_error_type: undefined,
                });
                toggleErrorModal();
            }
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const {value, name} = e.target;
        let error: string | undefined = '';

        if (name === 'password') {
            error = required(value);
        }

        setPassword(value);
        setError(error || '');
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const passwordError = required(password);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        const username = appLocalStorage.username.get() ?? '';

        const data = {
            username: username as MobilePhone,
            password: password as Password,
            onesignal_id: window.oneSignalInfo?.oneSignalUserId as NonEmptyString,
            onesignal_consent: window.oneSignalInfo?.oneSignalSubscribed,
        };
        signInAsync(data);
    };

    return (
        <div className={'flex flex-1 flex-col gap-6'}>
            <div className={'flex flex-col gap-2'}>
                <h1 className={cls.title}>Enter your password</h1>
                <p className={cls.subtitle}>Repeat your password to activate the</p>
                {biometryType === 'faceId' ? (
                    <p className={cls.subtitle}>Face ID function.</p>
                ) : (
                    <p className={cls.subtitle}>Touch ID function.</p>
                )}
            </div>
            <form onSubmit={handleSubmit} className='flex flex-1 flex-col justify-between'>
                <InputText
                    disabled={loading}
                    autoFocus={false}
                    name='password'
                    title='Password'
                    type='password'
                    apiError={error}
                    meta={{}}
                    input={{
                        name: 'password',
                        onChange: handleChange,
                    }}
                    showEye={!!password.length && !loading}
                />

                <RsnButton type='submit'>Continue</RsnButton>
            </form>

            <LoginErrorModal
                title={serverErrors.server_error}
                context={serverErrors.server_error_context}
                code={Number(serverErrors.server_error_code) || undefined}
                closeAction={closeErrorModal}
                callBackBtn={closeErrorModal}
                link={serverErrors.server_error_link}
                type={serverErrors.server_error_type}
            />
            <LoginBiometryStatusModal
                title={`${getBiometryTypeText()} is activated`}
                context='Raison app uses system biometrics.'
                closeAction={toggleBiometryStatusModal}
                callBackBtn={toggleBiometryStatusModal}
                isError={false}
                returnPoint='/settings'
            />
        </div>
    );
};

export default PasswordForm;

const cls = styles({
    title: 'fv-heading-l text-left text-neutral-100',
    subtitle: 'fv-text-l-regular text-left text-neutral-90',
});
